import { useCallback, useEffect, useRef, useState } from "react";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { Button } from "@humansignal/ui";
import { useToast } from "@humansignal/ui";
import { IconClose, IconSearch } from "@humansignal/icons";
import { Userpic } from "@humansignal/ui";
import { cn } from "../../../utils/bem";
import { qrSvgPath } from "../../../utils/qr";
import { useTranslation } from "../../../i18n/LocaleProvider";
import { UploadMembersTab } from "./UploadMembersTab";
import { EmailChipInput } from "../../../components/EmailChipInput/EmailChipInput";
import "./InviteModal.prefix.css";

const block = cn("invite-modal");

const ROLE_OPTIONS = [
  { value: "OW", label: "Owner" },
  { value: "AD", label: "Admin" },
  { value: "MA", label: "Manager" },
  { value: "RE", label: "Reviewer" },
  { value: "AN", label: "Annotator" },
  { value: "GU", label: "Guest" },
];

/** Public origin for shareable links. ``window.location.origin`` in the
 *  native app is ``capacitor://localhost`` — an invitee can't open
 *  that. Links must always carry the CLOUD host. */
const publicOrigin = () => {
  try {
    const cloud = window.APP_SETTINGS?.hostname;
    if (cloud) return cloud.replace(/\/$/, "");
  } catch (_) {
    /* fall through */
  }
  return window.location.origin;
};

// ── Invite Link tab ─────────────────────────────────────────────────────────────

const InviteLinkTab = ({ orgId }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showQr, setShowQr] = useState(true); // QR shown by default (onboarding sessions scan it)

  useEffect(() => {
    api.callApi("inviteLink").then((res) => {
      if (res?.invite_url) setLink(publicOrigin() + res.invite_url);
    });
  }, [api, orgId]);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [link]);

  const reset = useCallback(async () => {
    setResetting(true);
    const res = await api.callApi("resetInviteLink");
    setResetting(false);
    if (res?.invite_url) {
      setLink(publicOrigin() + res.invite_url);
      toast.show({ message: t("misc.invite_link_reset") });
    }
  }, [api, toast]);

  return (
    <div className={block.elem("link-tab").toClassName()}>
      <p className={block.elem("description").toClassName()}>
        Share this link to invite anyone to join your organization. Reset the link to invalidate the old one.
      </p>
      <div className={block.elem("link-row").toClassName()}>
        <input
          className={block.elem("link-input").toClassName()}
          value={link}
          readOnly
          onClick={(e) => e.target.select()}
        />
        <Button onClick={copy} size="small" variant={copied ? "positive" : "primary"}>
          {copied ? "Copied!" : "Copy"}
        </Button>
        <Button look="outlined" size="small" disabled={!link} onClick={() => setShowQr((v) => !v)}>
          {showQr ? "Hide QR" : "QR"}
        </Button>
      </div>
      {showQr && link && <InviteLinkQr url={link} />}
      <div className={block.elem("form-actions").toClassName()}>
        <Button look="outlined" variant="negative" size="small" disabled={resetting} onClick={reset}>
          {resetting ? "Resetting…" : "Reset link"}
        </Button>
      </div>
    </div>
  );
};

// Inline QR for the org invite link — participants at an onboarding
// session scan instead of typing. Rendered inline (not a stacked modal)
// because InviteModal is its own overlay. Black-on-white regardless of
// theme: scanners want the contrast.
const InviteLinkQr = ({ url }) => {
  const { size, d } = qrSvgPath(url);
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
      <svg
        width={240}
        height={240}
        viewBox={`0 0 ${size} ${size}`}
        shapeRendering="crispEdges"
        role="img"
        aria-label={url}
        style={{ background: "#fff", border: "1px solid var(--color-neutral-border, #e2e8f0)", borderRadius: 8 }}
      >
        <rect width={size} height={size} fill="#fff" />
        <path d={d} fill="#000" />
      </svg>
    </div>
  );
};

// ── Project pill selector ───────────────────────────────────────────────────────

const ProjectPillSelector = ({ orgId, selected, onChange }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const res = await api.callApi("projects", { params: { title: query } });
      setLoading(false);
      if (res?.results) {
        const selectedIds = new Set(selected.map((p) => p.id));
        setSuggestions(res.results.filter((p) => !selectedIds.has(p.id)).slice(0, 8));
      }
    }, 200);
  }, [query, api, selected]);

  const add = (project) => {
    onChange([...selected, { id: project.id, title: project.title }]);
    setQuery("");
    setSuggestions([]);
  };

  const remove = (id) => onChange(selected.filter((p) => p.id !== id));

  return (
    <div className={block.elem("project-selector").toClassName()}>
      <div className={block.elem("pills-container").toClassName()}>
        {selected.map((p) => (
          <span key={p.id} className={block.elem("pill").toClassName()}>
            {p.title}
            <button className={block.elem("pill-remove").toClassName()} onClick={() => remove(p.id)}>
              <IconClose style={{ width: 10, height: 10 }} />
            </button>
          </span>
        ))}
        <input
          className={block.elem("pill-input").toClassName()}
          placeholder={selected.length ? "" : "Search projects to assign…"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {(suggestions.length > 0 || loading) && (
        <div className={block.elem("suggestions").toClassName()}>
          {loading && <div className={block.elem("suggestion-item").toClassName()}>{t("misc.searching")}</div>}
          {suggestions.map((p) => (
            <div
              key={p.id}
              className={block.elem("suggestion-item").toClassName()}
              onMouseDown={(e) => {
                e.preventDefault();
                add(p);
              }}
            >
              {p.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Add by Email tab ─────────────────────────────────────────────────────────────

const AddByEmailTab = ({ org, onAdded }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const [emails, setEmails] = useState([]);
  const role = org?.default_role ?? "AN"; // starting role for each new row
  const [memberRoles, setMemberRoles] = useState({}); // per-address role (set on its row)
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Suggest people who already have an account but are not in this org yet.
  const suggestNonMembers = useCallback(
    async (q) => {
      if (!org?.id) return [];
      const res = await api.callApi("orgNonMembers", { params: { pk: org.id, search: q } });
      const list = Array.isArray(res) ? res : (res?.results ?? []);
      return list.map((u) => ({ email: u.email, first_name: u.first_name, last_name: u.last_name }));
    },
    [api, org?.id],
  );

  const submit = useCallback(async () => {
    if (!emails.length) return;
    setSubmitting(true);
    const res = await api.callApi("orgInviteByEmail", {
      params: { pk: org.id },
      body: {
        members: emails.map((email) => ({ email, role: memberRoles[email] || role })),
        role,
        project_ids: projects.map((p) => p.id),
      },
    });
    setSubmitting(false);
    if (res && !res.error) {
      setResult(res);
      setEmails([]);
      setProjects([]);
      onAdded?.();
      const added = res.added?.length ?? 0;
      const created = res.created?.length ?? 0;
      toast.show({
        message: `Added ${added} member${added !== 1 ? "s" : ""}${created ? ` (${created} new account${created !== 1 ? "s" : ""} created)` : ""}.`,
      });
    } else {
      toast.show({ message: res?.error ?? "Failed to add members", type: "error" });
    }
  }, [api, org, emails, role, memberRoles, projects, toast, onAdded]);

  return (
    <div className={block.elem("email-tab").toClassName()}>
      <p className={block.elem("description").toClassName()}>New addresses get an account and an activation email.</p>

      <div className={block.elem("form-field").mod({ grow: true }).toClassName()}>
        <label className={block.elem("form-label").toClassName()}>{t("misc.email_addresses")}</label>
        <EmailChipInput
          emails={emails}
          onChange={setEmails}
          suggest={suggestNonMembers}
          tall
          placeholder="Type an address and press Enter, or paste a list…"
          renderChipExtra={(email) => (
            <select
              className={cn("email-chip-input").elem("chip-select").toClassName()}
              value={memberRoles[email] || role}
              aria-label={`Role for ${email}`}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => setMemberRoles((m) => ({ ...m, [email]: e.target.value }))}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          )}
        />
      </div>

      <div className={block.elem("form-field").mod({ grow: true }).toClassName()}>
        <label className={block.elem("form-label").toClassName()}>{t("misc.assign_to_projects_optional")}</label>
        <ProjectPillSelector orgId={org.id} selected={projects} onChange={setProjects} />
      </div>

      {result && (
        <div className={block.elem("result").toClassName()}>
          {result.added?.length > 0 && (
            <div className={block.elem("result-row").mod({ ok: true }).toClassName()}>
              ✓ Added: {result.added.join(", ")}
            </div>
          )}
          {result.created?.length > 0 && (
            <div className={block.elem("result-row").mod({ ok: true }).toClassName()}>
              <div style={{ fontWeight: "var(--font-weight-semibold)", marginBottom: 6 }}>
                ✓ Created {result.created.length} new account{result.created.length !== 1 ? "s" : ""}. Share the setup
                link with each person:
              </div>
              <ul
                style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}
              >
                {result.created.map((c) => {
                  const email = typeof c === "string" ? c : c.email;
                  const url = typeof c === "string" ? null : c.set_password_url;
                  return (
                    <li key={email} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <strong style={{ minWidth: 140 }}>{email}</strong>
                      {url && (
                        <>
                          <input
                            readOnly
                            value={url}
                            onFocus={(e) => e.target.select()}
                            style={{
                              flex: 1,
                              padding: "4px 8px",
                              fontSize: "var(--font-size-caption)",
                              border: "1px solid var(--color-neutral-border)",
                              borderRadius: 4,
                            }}
                          />
                          <Button
                            type="button"
                            look="outlined"
                            variant="neutral"
                            size="small"
                            onClick={() => {
                              navigator.clipboard?.writeText(url);
                              toast.show({ message: t("misc.link_copied") });
                            }}
                          >
                            {t("misc.copy")}
                          </Button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {result.already_members?.length > 0 && (
            <div className={block.elem("result-row").toClassName()}>
              Already members: {result.already_members.join(", ")}
            </div>
          )}
          {result.errors?.length > 0 && (
            <div className={block.elem("result-row").mod({ error: true }).toClassName()}>
              Errors: {result.errors.join(", ")}
            </div>
          )}
        </div>
      )}

      <div className={block.elem("form-actions").toClassName()}>
        <Button onClick={submit} size="small" disabled={!emails.length || submitting} waiting={submitting}>
          {submitting ? "Adding…" : `Add ${emails.length || ""} member${emails.length !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
};

// ── Add Existing Users tab ───────────────────────────────────────────────────────

const AddExistingTab = ({ org, onAdded }) => {
  const api = useAPI();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  // Map of user id -> role for the selected people (role is picked per person).
  const [selected, setSelected] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef();
  const defaultRole = "AN";

  const seqRef = useRef(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const id = ++seqRef.current;
      setLoading(true);
      const res = await api.callApi("orgNonMembers", {
        params: search.trim() ? { pk: org.id, search: search.trim() } : { pk: org.id },
      });
      if (id !== seqRef.current) return; // a newer search superseded this one
      setLoading(false);
      setLoaded(true);
      if (res && !res.error) setUsers(res.results ?? res);
    }, 250);
  }, [api, org.id, search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.has(id) ? next.delete(id) : next.set(id, defaultRole);
      return next;
    });
  };
  const setMemberRole = (id, r) => setSelected((prev) => new Map(prev).set(id, r));

  const submit = useCallback(async () => {
    if (!selected.size) return;
    setSubmitting(true);
    const res = await api.callApi("orgAddMembers", {
      params: { pk: org.id },
      body: {
        members: [...selected.entries()].map(([user_id, r]) => ({ user_id, role: r })),
        role: defaultRole,
      },
    });
    setSubmitting(false);
    if (res && !res.error) {
      toast.show({ message: `Added ${res.added?.length ?? 0} member(s)` });
      setSelected(new Map());
      onAdded?.();
      // Refresh list
      const refresh = await api.callApi("orgNonMembers", { params: { pk: org.id } });
      if (refresh && !refresh.error) setUsers(refresh.results ?? refresh);
    } else {
      toast.show({ message: res?.error ?? "Failed", type: "error" });
    }
  }, [api, org, selected, toast, onAdded]);

  return (
    <div className={block.elem("existing-tab").toClassName()}>
      <p className={block.elem("description").toClassName()}>
        Select from members of the other organizations you manage.
      </p>

      <div className={block.elem("search-row").toClassName()}>
        <div className={block.elem("search-wrap").toClassName()}>
          <IconSearch className={block.elem("search-icon").toClassName()} />
          <input
            className={block.elem("search-input").toClassName()}
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div
        className={block
          .elem("user-list")
          .mod({ busy: loading && loaded })
          .toClassName()}
      >
        {loading && !loaded && <div className={block.elem("user-empty").toClassName()}>Loading…</div>}
        {loaded && !loading && users.length === 0 && (
          <div className={block.elem("user-empty").toClassName()}>
            {search ? "No matching users found." : "All registered users are already members."}
          </div>
        )}
        {users.map((u) => {
          const name = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;
          return (
            <label
              key={u.id}
              className={block
                .elem("user-row")
                .mod({ selected: selected.has(u.id) })
                .toClassName()}
            >
              <input
                type="checkbox"
                checked={selected.has(u.id)}
                onChange={() => toggle(u.id)}
                className={block.elem("user-checkbox").toClassName()}
              />
              <Userpic user={u} style={{ width: 28, height: 28, flexShrink: 0 }} />
              <div className={block.elem("user-info").toClassName()}>
                <span className={block.elem("user-name").toClassName()}>{name}</span>
                <span className={block.elem("user-email").toClassName()}>{u.email}</span>
              </div>
              {selected.has(u.id) && (
                <select
                  className={block.elem("select").mod({ row: true }).toClassName()}
                  value={selected.get(u.id)}
                  aria-label={`Role for ${name}`}
                  onClick={(e) => e.preventDefault()}
                  onChange={(e) => setMemberRole(u.id, e.target.value)}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              )}
            </label>
          );
        })}
      </div>

      {selected.size > 0 && (
        <div className={block.elem("form-actions").toClassName()}>
          <Button onClick={submit} size="small" disabled={submitting} waiting={submitting}>
            {submitting ? "Adding…" : `Add ${selected.size} selected`}
          </Button>
          <Button look="outlined" size="small" onClick={() => setSelected(new Map())}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

// ── Modal root ──────────────────────────────────────────────────────────────────

const TABS = ["Invite link", "By email", "Upload file", "Existing users"];
// Short labels for narrow screens (the full label is the tab's key).
const TAB_SHORT = { "Invite link": "Link", "By email": "Email", "Upload file": "Upload", "Existing users": "Existing" };

export const InviteModal = ({ opened, onClosed, onMembersAdded }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const api = useAPI();
  const [activeTab, setActiveTab] = useState("Invite link");
  const [org, setOrg] = useState(null);

  useEffect(() => {
    if (!user?.active_organization) return;
    const activeOrgId = user.active_organization;
    api.callApi("organizations").then((res) => {
      const list = Array.isArray(res) ? res : (res?.results ?? []);
      const found = list.find((o) => o.id === activeOrgId);
      if (found) {
        setOrg(found);
        return;
      }
      // The list endpoint is scoped to orgs the caller is a member of —
      // even for staff/superuser. Without this fallback a superuser who
      // switched into an org they don't belong to would never resolve
      // `org` and this modal would spin "Loading…" forever. Fall back to
      // the staff-scoped detail API so the invite UI opens.
      api.callApi("getOrganization", { params: { pk: activeOrgId } }).then((one) => {
        if (one && !one.error && one.id === activeOrgId) setOrg(one);
      });
    });
  }, [api, user]);

  if (!opened) return null;

  return (
    <div className={block.elem("overlay").toClassName()} onClick={(e) => e.target === e.currentTarget && onClosed?.()}>
      <div className={block.toClassName()}>
        <div className={block.elem("header").toClassName()}>
          <h2 className={block.elem("title").toClassName()}>{t("misc.add_members")}</h2>
          <button className={block.elem("close").toClassName()} onClick={onClosed}>
            <IconClose style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div className={block.elem("tabs").toClassName()}>
          {TABS.map((tabName) => (
            <button
              key={tabName}
              className={block
                .elem("tab")
                .mod({ active: tabName === activeTab })
                .toClassName()}
              onClick={() => setActiveTab(tabName)}
              title={tabName}
            >
              <span className={block.elem("tab-full").toClassName()}>{tabName}</span>
              <span className={block.elem("tab-short").toClassName()}>{TAB_SHORT[tabName] ?? tabName}</span>
            </button>
          ))}
        </div>

        <div className={block.elem("content").toClassName()}>
          {!org ? (
            <div style={{ padding: 24, color: "var(--color-neutral-content-subtler)" }}>Loading…</div>
          ) : activeTab === "Invite link" ? (
            <InviteLinkTab orgId={org.id} />
          ) : activeTab === "By email" ? (
            <AddByEmailTab org={org} onAdded={onMembersAdded} />
          ) : activeTab === "Upload file" ? (
            <UploadMembersTab org={org} onAdded={onMembersAdded} />
          ) : (
            <AddExistingTab org={org} onAdded={onMembersAdded} />
          )}
        </div>
      </div>
    </div>
  );
};
